import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatDate,
  getAllJSON,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";
import { detectPii } from "../pii.js";

// Panel leadów personelu. NAJWAŻNIEJSZY niezmiennik: PII NIE jest renderowane masowo.
// Lista pokazuje wyłącznie 0-PII (id, rodzaj, region, typ) — nawet gdy API zwraca pełne PII
// uprawnionemu personelowi. Dane kontaktowe ujawnia wyłącznie jawna akcja „Pokaż dane"
// (retrieve pojedynczego leada → audyt lead.pii_viewed po stronie backendu). Import idzie
// przez jedyną ścieżkę zapisu import_lead (POST /api/leads/, odpowiedź 0-PII).
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "banner",
    "modal",
    "modalBody",
    "modalError",
    "form",
    "formError",
    "voivSelect",
    "countySelect",
    "search",
    "kindFilter",
    "typeFilter",
    "voivFilter",
    "pagination",
    "pageInfo",
    "prevBtn",
    "nextBtn",
  ];
  static values = {
    url: String, // /api/leads/
    voivodeshipsUrl: String, // /api/voivodeships/
    countiesUrl: String, // /api/counties/
    canViewPii: Boolean, // core.view_lead_pii
    canAdd: Boolean, // core.add_lead
  };

  static KIND_LABEL = { firma: "Firma", osoba: "Osoba" };

  connect() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = this.canViewPiiValue;
    this.page = 1; // aktualna strona paginacji (server-side, DRF PageNumberPagination)
    this.load();
    this.#loadVoivodeships();
  }

  // Zmiana search/filtra → zawsze wracamy na stronę 1 i przeładowujemy listę.
  applyFilters() {
    this.page = 1;
    this.load();
  }

  // Debounce na wpisywaniu w polu szukania (change/klik „Szukaj" idą przez applyFilters).
  searchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.applyFilters(), 300);
  }

  prevPage() {
    if (this.page > 1) {
      this.page -= 1;
      this.load();
    }
  }

  nextPage() {
    if (this.hasNextPage) {
      this.page += 1;
      this.load();
    }
  }

  async load() {
    let data;
    try {
      data = await getJSON(this.#listUrl());
    } catch {
      this.#status("Nie udało się załadować leadów. Odśwież stronę.");
      return;
    }
    const leads = listResults(data);
    this.rowsTarget.textContent = "";
    this.#renderPagination(data);
    if (!leads.length) {
      this.#status("Brak leadów.");
      return;
    }
    this.#status(null);
    const template = document.getElementById("lead-admin-row-template");
    for (const lead of leads) {
      const row = template.content.firstElementChild.cloneNode(true);
      // ŚWIADOMIE renderujemy TYLKO pola 0-PII — nigdy name/nip/phone/email/city,
      // nawet jeśli backend je zwrócił uprawnionemu personelowi.
      row.querySelector('[data-field="id"]').textContent = `#${lead.id}`;
      row.querySelector('[data-field="kind"]').textContent =
        this.constructor.KIND_LABEL[lead.kind] ?? lead.kind;
      row.querySelector('[data-field="region"]').textContent =
        `woj. ${lead.voivodeship} / pow. ${lead.county}`;
      row.querySelector('[data-field="type"]').textContent = lead.lead_type;

      const reveal = row.querySelector('[data-field="reveal"]');
      if (reveal) {
        if (!this.canViewPiiValue) {
          reveal.hidden = true; // brak uprawnienia = brak przycisku ujawniania
        } else {
          reveal.hidden = false;
          reveal.setAttribute("data-action", "lead-admin#reveal");
          reveal.setAttribute("data-lead-admin-id-param", lead.id);
        }
      }
      this.rowsTarget.appendChild(row);
    }
  }

  // Jawne ujawnienie PII jednego leada = retrieve (backend audytuje lead.pii_viewed).
  async reveal(event) {
    const id = event.params.id;
    if (this.hasModalErrorTarget) this.modalErrorTarget.hidden = true;
    if (this.hasModalBodyTarget)
      this.modalBodyTarget.textContent = "Wczytywanie danych…";
    if (this.hasModalTarget) this.modalTarget.hidden = false;
    let lead;
    try {
      lead = await getJSON(`${this.urlValue}${id}/`);
    } catch {
      this.#modalError("Nie udało się pobrać danych leada.");
      return;
    }
    this.#renderPii(lead);
  }

  async importLead(event) {
    event.preventDefault();
    const form = this.hasFormTarget
      ? this.formTarget
      : event.currentTarget.closest("form");
    const payload = this.#collect(form);
    // Blokada submitu: notatka (teaser widoczny klientowi) nie może zawierać PII.
    // Twardą gwarancję daje backstop serwerowy (400), tu natychmiastowy feedback.
    const noteHits = detectPii(payload.note || "");
    if (noteHits.length) {
      this.#formError(
        `Usuń dane kontaktowe z notatki (wykryto: ${noteHits.join(", ")}).`,
      );
      return;
    }
    const res = await sendJSON("POST", this.urlValue, payload);
    if (!res.ok) {
      this.#formError(
        this.#fieldErrors(res.data) ||
          errorDetail(res.data, "Nie udało się dodać leada."),
      );
      return;
    }
    this.#formError(null);
    form.reset();
    if (this.hasCountySelectTarget) this.countySelectTarget.disabled = true;
    await this.load();
  }

  async loadCounties() {
    if (!this.hasCountySelectTarget) return;
    const voivodeship = this.hasVoivSelectTarget
      ? this.voivSelectTarget.value
      : "";
    this.countySelectTarget.replaceChildren(new Option("—", ""));
    if (!voivodeship) {
      this.countySelectTarget.disabled = true;
      return;
    }
    let counties;
    try {
      counties = await getAllJSON(
        `${this.countiesUrlValue}?voivodeship=${voivodeship}`,
      );
    } catch {
      return;
    }
    for (const county of counties) {
      const option = document.createElement("option");
      option.value = county.id;
      option.textContent = county.name;
      this.countySelectTarget.appendChild(option);
    }
    this.countySelectTarget.disabled = false;
  }

  closeModal() {
    if (this.hasModalTarget) this.modalTarget.hidden = true;
  }

  stop(event) {
    event.stopPropagation();
  }

  // --- prywatne ---

  #renderPii(lead) {
    if (!this.hasModalBodyTarget) return;
    this.modalBodyTarget.textContent = "";
    const rows = [
      ["Nazwa", lead.name],
      ["NIP", lead.nip || "—"],
      ["Telefon", lead.phone],
      ["E-mail", lead.email],
      ["Miasto", lead.city_text || "—"],
      ["Źródło", lead.acquisition_source],
      ["Pozyskano", formatDate(lead.acquired_at)],
    ];
    for (const [label, value] of rows) {
      const line = document.createElement("div");
      line.style.cssText =
        "display:flex;justify-content:space-between;gap:16px;padding:6px 0;border-bottom:1px solid var(--color-ls-line-2);font-size:14px;";
      const key = document.createElement("span");
      key.style.color = "var(--color-ls-muted)";
      key.textContent = label;
      const val = document.createElement("span");
      val.className = "ls-mono";
      val.textContent = value ?? "—";
      line.append(key, val);
      this.modalBodyTarget.appendChild(line);
    }
  }

  #collect(form) {
    const payload = {};
    for (const input of form.querySelectorAll("[name]")) {
      if (input.value === "") continue; // pomiń puste opcjonalne (nip/city)
      payload[input.name] = input.value;
    }
    return payload;
  }

  #fieldErrors(data) {
    if (!data || typeof data !== "object" || data.detail) return null;
    const parts = [];
    for (const [field, messages] of Object.entries(data)) {
      const text = Array.isArray(messages)
        ? messages.join(" ")
        : String(messages);
      parts.push(`${field}: ${text}`);
    }
    return parts.length ? parts.join(" · ") : null;
  }

  // Buduje URL listy z bieżącym stanem paginacji i filtrów:
  // /api/leads/?page=N&search=…&kind=…&lead_type=…&voivodeship=…
  #listUrl() {
    const query = new URLSearchParams();
    if (this.page > 1) query.set("page", this.page);
    const search = this.hasSearchTarget ? this.searchTarget.value.trim() : "";
    if (search) query.set("search", search);
    const kind = this.hasKindFilterTarget ? this.kindFilterTarget.value : "";
    if (kind) query.set("kind", kind);
    const leadType = this.hasTypeFilterTarget ? this.typeFilterTarget.value : "";
    if (leadType) query.set("lead_type", leadType);
    const voivodeship = this.hasVoivFilterTarget
      ? this.voivFilterTarget.value
      : "";
    if (voivodeship) query.set("voivodeship", voivodeship);
    const text = query.toString();
    return text ? `${this.urlValue}?${text}` : this.urlValue;
  }

  // Info „strona X (Y leadów)" + przyciski ‹ Poprzednia / Następna › na bazie count/next/previous.
  #renderPagination(data) {
    this.hasNextPage = Boolean(data.next);
    const hasPrev = Boolean(data.previous);
    if (this.hasPageInfoTarget) {
      const count = data.count ?? listResults(data).length;
      this.pageInfoTarget.textContent = `strona ${this.page} (${count} leadów)`;
    }
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = !hasPrev;
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !this.hasNextPage;
    if (this.hasPaginationTarget) {
      // Ukryj pasek stron, gdy jest tylko jedna strona wyników.
      this.paginationTarget.hidden = !(this.hasNextPage || hasPrev);
    }
  }

  async #loadVoivodeships() {
    const wantsAddSelect = this.hasVoivSelectTarget;
    const wantsFilterSelect = this.hasVoivFilterTarget;
    if (!wantsAddSelect && !wantsFilterSelect) return;
    let voivodeships;
    try {
      voivodeships = await getAllJSON(this.voivodeshipsUrlValue);
    } catch {
      return;
    }
    for (const voivodeship of voivodeships) {
      if (wantsAddSelect) {
        const option = document.createElement("option");
        option.value = voivodeship.id;
        option.textContent = voivodeship.name;
        this.voivSelectTarget.appendChild(option);
      }
      if (wantsFilterSelect) {
        const option = document.createElement("option");
        option.value = voivodeship.id;
        option.textContent = voivodeship.name;
        this.voivFilterTarget.appendChild(option);
      }
    }
  }

  #modalError(message) {
    if (this.hasModalBodyTarget) this.modalBodyTarget.textContent = "";
    if (!this.hasModalErrorTarget) return;
    this.modalErrorTarget.hidden = false;
    this.modalErrorTarget.textContent = message;
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return;
    this.formErrorTarget.hidden = message === null;
    if (message !== null) this.formErrorTarget.textContent = message;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
